import React, { useCallback } from 'react'
import { SpeakerMaxIcon } from '@/atoms/Icons/SpeakerMaxIcon'
import { SpeakerMuteIcon } from '@/atoms/Icons/SpeakerMuteIcon'
import { VOLUME_MAX, VOLUME_MIN } from '@/constants/video'
import { useHlsPlayerUI } from './HlsPlayerUIContext'

/**
 * Volume mute toggle + always-visible slider.
 * Hidden on mobile (touch devices) since mobile volume is controlled by hardware buttons.
 */
export const HlsVolumeControl: React.FC = () => {
  const { volume, muted, controller } = useHlsPlayerUI()

  const isMuted = muted || volume === VOLUME_MIN
  const displayVolume = isMuted ? VOLUME_MIN : volume

  const handleToggleMute = useCallback(() => {
    if (!controller) return
    if (isMuted) {
      controller.unmute()
      if (volume === VOLUME_MIN) {
        controller.setVolume(VOLUME_MAX)
      }
    } else {
      controller.mute()
    }
  }, [controller, isMuted, volume])

  const handleVolumeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (!controller) return
      const newVolume = Number(e.target.value)
      controller.setVolume(newVolume)
      if (newVolume > VOLUME_MIN && muted) {
        controller.unmute()
      }
    },
    [controller, muted],
  )

  return (
    <div className="hidden items-center gap-1.5 md:flex">
      <button
        type="button"
        className="flex h-8 w-8 items-center justify-center rounded text-white transition-colors hover:bg-white/20"
        onClick={handleToggleMute}
        aria-label={isMuted ? 'Unmute' : 'Mute'}
      >
        {isMuted ? (
          <SpeakerMuteIcon size={18} color="core-gray-100" />
        ) : (
          <SpeakerMaxIcon size={18} color="core-gray-100" />
        )}
      </button>

      <div className="group relative flex w-16 items-center">
        {/* Track background */}
        <div className="h-1 w-full rounded-full bg-white/30">
          {/* Fill */}
          <div className="h-full rounded-full bg-white" style={{ width: `${displayVolume}%` }} />
        </div>
        {/* Thumb */}
        <div
          className="pointer-events-none absolute h-2.5 w-2.5 -translate-x-1/2 rounded-full bg-white shadow"
          style={{ left: `${displayVolume}%` }}
        />
        {/* Invisible range input for interaction */}
        <input
          type="range"
          min={VOLUME_MIN}
          max={VOLUME_MAX}
          value={displayVolume}
          onChange={handleVolumeChange}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
          aria-label="Volume"
        />
      </div>
    </div>
  )
}
